/**
 * Combined Login / Signup page. Single form with a toggle so users can
 * flip between the two without a route change. v1 spec: identifier is
 * either email or phone; password + confirm; no verification.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Footer from "../components/Footer";
import SiteHeader from "../components/SiteHeader";
import PasswordInput from "../components/PasswordInput";

export default function AuthPage({ mode: initialMode = "login" }) {
  const navigate = useNavigate();
  const { signup, login } = useAuth();
  const [mode, setMode] = useState(initialMode); // "login" | "signup"
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const idt = identifier.trim();
    if (!idt) {
      toast.error("Enter your email or phone");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (mode === "signup" && password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await signup({ identifier: idt, password, name: name.trim() || null });
        toast.success("Account created. Welcome!");
      } else {
        await login({ identifier: idt, password });
        toast.success("Logged in");
      }
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const isSignup = mode === "signup";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="pt-24 px-6 max-w-md mx-auto w-full flex-1">
        <h1 className="serif text-3xl text-[#1A1A1A] mb-2" data-testid="auth-title">
          {isSignup ? "Create your account" : "Welcome back"}
        </h1>
        <p className="text-sm text-[#6B6B6B] mb-6">
          {isSignup
            ? "Save your portraits and reorder anytime."
            : "Log in to see your saved portraits and orders."}
        </p>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
          {isSignup && (
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-[#EAE5DF] px-4 py-3 bg-card text-[#1A1A1A]"
              data-testid="auth-name"
            />
          )}
          <input
            type="text"
            placeholder="Email or phone number"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="w-full border border-[#EAE5DF] px-4 py-3 bg-card text-[#1A1A1A]"
            autoComplete="username"
            required
            data-testid="auth-identifier"
          />
          <PasswordInput
            placeholder="Password (6+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border border-[#EAE5DF] px-4 py-3 bg-card text-[#1A1A1A]"
            autoComplete={isSignup ? "new-password" : "current-password"}
            required
            testId="auth-password"
          />
          {isSignup && (
            <PasswordInput
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="border border-[#EAE5DF] px-4 py-3 bg-card text-[#1A1A1A]"
              autoComplete="new-password"
              required
              testId="auth-confirm-password"
            />
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-accent w-full h-12 disabled:opacity-60"
            data-testid="auth-submit"
          >
            {submitting
              ? "Please wait…"
              : isSignup
                ? "Create account"
                : "Log in"}
          </button>
        </form>
        <p className="text-sm text-[#6B6B6B] text-center mt-6">
          {isSignup ? "Already have an account?" : "New here?"}{" "}
          <button
            type="button"
            onClick={() => setMode(isSignup ? "login" : "signup")}
            className="text-[#9B6A2D] underline"
            data-testid="auth-toggle-mode"
          >
            {isSignup ? "Log in" : "Sign up"}
          </button>
        </p>
        <p className="text-xs text-[#9A9A9A] text-center mt-4">
          By continuing you agree to{" "}
          <Link to="/terms" className="underline">terms</Link>{" "}
          &amp;{" "}
          <Link to="/privacy" className="underline">privacy</Link>.
        </p>
      </main>
      <Footer />
    </div>
  );
}
